import { EMPTY, merge, Observable } from 'rxjs';
import { catchError, filter, ignoreElements, map, mergeMap, pairwise, scan } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AnyAction } from 'typescript-fsa';
import { IEpic, isTypeOf, isTypeOfAndGetPayload } from '@cbridge-utils/redux-observable';
import { ClpConnectionState, IClpApiDependencySegment } from '@cbridge-packages/api-clp';
import { SymbolQuotesAction } from '../symbolQuotes.action';
import { lpQuoteKey } from '../symbolQuotes.constant';
import { ILpSymbolKey } from '../symbolQuotes.interface';

// The visible-viewport set is the desired subscription set. Each epic keeps the previous set in an
// RxJS `scan` (not the store — epics see post-reducer state, which would defeat a state-based diff)
// and on every change subscribes the arrivals / unsubscribes the departures.
//
// The diff is NOT gated on `connect`: the visible-set actions and the open of the socket race during
// initial load, and the ClpApiService buffers outgoing frames (outbox) until the socket opens — so a
// subscription requested before the socket is open is delivered on open. A `disconnect` resets the
// diff to empty, so the next visible-set emission after a (re)connect re-subscribes the whole set.
//
// A transport-level auto-reconnect (`Reconnecting` → `Open`) is a NEW CLP session — server-side
// subscriptions are gone while the diff still holds them. On that transition the whole current set
// is re-subscribed (RESEND). The initial `Connecting` → `Open` is excluded: the outbox already
// delivers frames sent before the first open (a first open after a failed attempt may thus subscribe
// twice — harmless, the server subscription set is keyed).

const RESEND = 'resend';

const reconnected$ = (action$: Observable<AnyAction>): Observable<typeof RESEND> =>
  action$.pipe(
    isTypeOfAndGetPayload(SymbolQuotesAction.connectionStateChanged),
    map(({ state }) => state),
    pairwise(),
    filter(([previous, current]) => previous === ClpConnectionState.Reconnecting && current === ClpConnectionState.Open),
    map(() => RESEND),
  );

interface IClientDiffState {
  prev: Set<string>;
  add: string[];
  remove: string[];
  marketDepth: number;
}

const CLIENT_DIFF_SEED: IClientDiffState = { prev: new Set(), add: [], remove: [], marketDepth: 0 };

const clientDiff = (acc: IClientDiffState, payload: { symbols: string[]; marketDepth: number }): IClientDiffState => {
  const next = new Set(payload.symbols);
  return {
    prev: next,
    add: payload.symbols.filter((symbol) => !acc.prev.has(symbol)),
    remove: [...acc.prev].filter((symbol) => !next.has(symbol)),
    marketDepth: payload.marketDepth,
  };
};

const clientResend = (acc: IClientDiffState): IClientDiffState => ({ ...acc, add: [...acc.prev], remove: [] });

export const subscribeClientDiffEpic: IEpic<{}, IClpApiDependencySegment> = (action$, _, { clpApi }) => {
  const reset$: Observable<null> = action$.pipe(isTypeOf(SymbolQuotesAction.disconnect), map(() => null));
  const visible$ = action$.pipe(isTypeOfAndGetPayload(SymbolQuotesAction.setVisibleClientSymbols));

  return merge(reset$, visible$, reconnected$(action$)).pipe(
    scan((acc, event) => {
      if (event === null) {
        return CLIENT_DIFF_SEED;
      }
      return event === RESEND ? clientResend(acc) : clientDiff(acc, event);
    }, CLIENT_DIFF_SEED),
    mergeMap(({ add, remove, marketDepth }) =>
      merge(
        add.length
          ? clpApi.subscribe$({
            requestId: uuidv4(),
            clientSymbols: add.map((symbol) => ({ symbol, marketDepth })),
            lpSymbols: [],
          }).pipe(ignoreElements(), catchError(() => EMPTY))
          : EMPTY,
        remove.length
          ? clpApi.unsubscribe$({ requestId: uuidv4(), clientSymbols: remove, lpSymbols: [] })
            .pipe(ignoreElements(), catchError(() => EMPTY))
          : EMPTY,
      ),
    ),
  );
};

interface ILpDiffState {
  prev: Map<string, ILpSymbolKey>;
  add: ILpSymbolKey[];
  remove: ILpSymbolKey[];
  marketDepth: number;
}

const LP_DIFF_SEED: ILpDiffState = { prev: new Map(), add: [], remove: [], marketDepth: 0 };

const lpDiff = (acc: ILpDiffState, payload: { items: ILpSymbolKey[]; marketDepth: number }): ILpDiffState => {
  const next = new Map(payload.items.map((item) => [lpQuoteKey(item), item] as const));
  return {
    prev: next,
    add: [...next].filter(([key]) => !acc.prev.has(key)).map(([, item]) => item),
    remove: [...acc.prev].filter(([key]) => !next.has(key)).map(([, item]) => item),
    marketDepth: payload.marketDepth,
  };
};

const lpResend = (acc: ILpDiffState): ILpDiffState => ({ ...acc, add: [...acc.prev.values()], remove: [] });

export const subscribeLpDiffEpic: IEpic<{}, IClpApiDependencySegment> = (action$, _, { clpApi }) => {
  const reset$: Observable<null> = action$.pipe(isTypeOf(SymbolQuotesAction.disconnect), map(() => null));
  const visible$ = action$.pipe(isTypeOfAndGetPayload(SymbolQuotesAction.setVisibleLpSymbols));

  return merge(reset$, visible$, reconnected$(action$)).pipe(
    scan((acc, event) => {
      if (event === null) {
        return LP_DIFF_SEED;
      }
      return event === RESEND ? lpResend(acc) : lpDiff(acc, event);
    }, LP_DIFF_SEED),
    mergeMap(({ add, remove, marketDepth }) =>
      merge(
        add.length
          ? clpApi.subscribe$({
            requestId: uuidv4(),
            clientSymbols: [],
            lpSymbols: add.map(({ lpId, coreSymbolId }) => ({ lpId, coreSymbolId, marketDepth })),
          }).pipe(ignoreElements(), catchError(() => EMPTY))
          : EMPTY,
        remove.length
          ? clpApi.unsubscribe$({
            requestId: uuidv4(),
            clientSymbols: [],
            lpSymbols: remove.map(({ lpId, coreSymbolId }) => ({ lpId, coreSymbolId })),
          }).pipe(ignoreElements(), catchError(() => EMPTY))
          : EMPTY,
      ),
    ),
  );
};
